// src/components/eframes/EFrame247.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui/ResponsiveLayout";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import { unifiedContactService } from "../../services/unifiedContactService";
import { assetAggregationService, type UnifiedAsset } from "../../services/assetAggregationService";
import { isFullyAssigned } from "../../utils/distributionUtils";
import { SubHeadline } from "../ui/Typography";
import { Card } from "../ui";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface BeneficiaryInfo {
  contactId: string;
  name: string;
  percentage?: number;
}

interface AssetDistribution {
  id: string;
  assetId: string;
  assetName: string;
  assetCategory: string;
  primaryBeneficiaries: BeneficiaryInfo[];
  contingentBeneficiaries: BeneficiaryInfo[];
  instructions?: string;
  notes?: string;
}

const EFrame247: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);
  const [distributions, setDistributions] = useState<AssetDistribution[]>([]);

  useEffect(() => {
    const loadData = async () => {
      const rawDistributions =
        session.taskProgress?.assetDistribution?.answers?.distributions || {};

      const assets = assetAggregationService.getAllAssets(session, { useLocalBackups: false });

      let contacts: unknown[] = [];
      try {
        contacts = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();
      } catch {
        contacts = [];
      }

      const contactMap = new Map(contacts.map((c) => [c.id, c]));

      const resolveContact = (id: string, pct: number): BeneficiaryInfo | null => {
        const c = contactMap.get(id);
        if (!c) return null;
        return {
          contactId: id,
          name: `${c.firstName} ${c.lastName}`.trim(),
          percentage: pct,
        };
      };

      const distArray = Array.isArray(rawDistributions)
        ? rawDistributions
        : Object.values(rawDistributions);

      const manuallyAssignedIds = new Set<string>();

      const hydrated = distArray
        .map((d: unknown) => {
          const asset = assets.find((a: UnifiedAsset) => a.id === d.assetId);
          if (!asset) return null;

          manuallyAssignedIds.add(d.assetId);

          const primary =
            d.primaryBeneficiaries?.map((pb: unknown) =>
              resolveContact(pb.beneficiaryId, pb.percentage ?? 100)
            ).filter(Boolean) || [];

          return {
            id: d.assetId,
            assetId: d.assetId,
            assetName: asset.name || "Unknown Asset",
            assetCategory: asset.category || "personal",
            primaryBeneficiaries: primary,
            contingentBeneficiaries: [],
          };
        })
        .filter(Boolean) as AssetDistribution[];

      const preAssigned = assets
        .filter(
          (a) =>
            !manuallyAssignedIds.has(a.id) &&
            a.existingBeneficiaries?.length
        )
        .map((asset) => ({
          id: asset.id,
          assetId: asset.id,
          assetName: asset.name || "Unknown Asset",
          assetCategory: asset.category || "personal",
          primaryBeneficiaries: asset.existingBeneficiaries!
            .map((eb) => resolveContact(eb.contactId, eb.percentage || 100))
            .filter(Boolean) as BeneficiaryInfo[],
          contingentBeneficiaries: [],
        }));

      setDistributions([...hydrated, ...preAssigned]);
    };

    if (session) loadData();
  }, [estateId, session]);

  const assignedDistributions = useMemo(
    () => distributions.filter((d) => isFullyAssigned(d)),
    [distributions]
  );

  const byBeneficiary = assignedDistributions.reduce((acc: unknown, dist) => {
    dist.primaryBeneficiaries.forEach((pb) => {
      if (!acc[pb.name]) acc[pb.name] = [];
      acc[pb.name].push(dist);
    });
    return acc;
  }, {});

  const byCategory = assignedDistributions.reduce((acc: unknown, dist) => {
    if (!acc[dist.assetCategory]) acc[dist.assetCategory] = [];
    acc[dist.assetCategory].push(dist);
    return acc;
  }, {});

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="space-y-6">
          <div className="mb-6 flex justify-start">
            <BackToExecutorDashboardButton />
          </div>
          <ResponsiveHeader title={`${planOwnerName}'s Asset Distribution Summary`} />

          <SubHeadline>
            Review how {planOwnerName}'s assets are intended to be shared. 
            This summary helps you understand the distribution plan you will eventually oversee. 
          </SubHeadline>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Overview</h3>
            <div className="grid grid-cols-3 text-center">
              <div>
                <div className="text-2xl font-bold">
                  {assignedDistributions.length}
                </div>
                <div className="text-sm">Assets</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {Object.keys(byBeneficiary).length}
                </div>
                <div className="text-sm">Beneficiaries</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {Object.keys(byCategory).length}
                </div>
                <div className="text-sm">Categories</div>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">By Beneficiary</h3>
            {Object.entries(byBeneficiary).map(([name, dists]: unknown) => (
              <div key={name} className="mb-6">
                <div className="font-semibold mb-2">{name}</div>
                {dists.map((d: AssetDistribution) => (
                  <div key={d.id} className="text-sm text-stone-600">
                    {d.assetName}
                  </div>
                ))}
              </div>
            ))}
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">By Category</h3>
            {Object.entries(byCategory).map(([cat, dists]: unknown) => (
              <div key={cat} className="mb-6">
                <div className="font-semibold mb-2 capitalize">{cat}</div>
                {dists.map((d: AssetDistribution) => (
                  <div key={d.id} className="text-sm text-stone-600">
                    {d.assetName}
                  </div>
                ))}
              </div>
            ))}
          </Card>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame247;